import { Lightning, Utils } from "@lightningjs/sdk";
import theme from "../../../lib/theme";

interface MaterialIconTemplateSpec extends Lightning.Component.TemplateSpec {
    w: number;
    h: number;
    icon: string;
    // Using colorValue instead of color to avoid conflict with Lightning Component property
    colorValue: string;
    Icon: object;
}

export class MaterialIcon
    extends Lightning.Component<MaterialIconTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MaterialIconTemplateSpec>
{
    private _icon: string = "";
    private _colorHex: string = theme.colors.text.primary;
    private _iconSize: number = 24;

    static override _template(): Lightning.Component.Template<MaterialIconTemplateSpec> {
        return {
            w: 24,
            h: 24,
            Icon: {}
        };
    }

    set icon(value: string) {
        this._icon = value;
        this._updateIcon();
    }

    set colorValue(value: string) {
        this._colorHex = value;
        this._updateIcon();
    }

    override set w(value: number) {
        this._iconSize = value;
        this.h = value; // Keep icon square unless explicitly set otherwise
        this._updateIcon();
    }

    _updateIcon() {
        if (!this._icon) return;

        // First, fetch the SVG content
        this._fetchSvg(this._icon).then((svgContent) => {
            if (!svgContent) return;

            // Replace the fill color
            const coloredSvg = this._replaceColor(svgContent, this._colorHex);

            // Create a data URL
            const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(coloredSvg)}`;

            // Load the SVG as a texture
            const texture = Lightning.Tools.getSvgTexture(dataUrl, this._iconSize, this._iconSize);

            // Apply texture to the icon
            const iconTag = this.tag("Icon");
            if (iconTag) {
                iconTag.patch({
                    texture
                });
            }
        });
    }

    private async _fetchSvg(iconName: string): Promise<string | null> {
        try {
            // Fetch SVG from assets
            const response = await fetch(Utils.asset(`icons/${iconName}.svg`));
            if (!response.ok) {
                console.error(`Error loading SVG: ${response.statusText}`);
                return null;
            }
            return await response.text();
        } catch (err) {
            console.error(`Error loading SVG: ${err}`);
            return null;
        }
    }

    private _replaceColor(svgContent: string, color: string): string {
        // Replace all fill attributes, but keep any explicit "fill: none" intact
        return (
            svgContent
                .replace(/fill="(?!none)[^"]*"/g, `fill="${color}"`)
                .replace(/fill="none"/g, 'fill="none"')
                // Also handle style-based fills
                .replace(/fill:[^;]+;/g, `fill:${color};`)
                .replace(/stroke="(?!none)[^"]*"/g, `stroke="${color}"`)
                .replace(/stroke="none"/g, 'stroke="none"')
                // Also handle style-based fills
                .replace(/stroke:[^;]+;/g, `stroke:${color};`)
        );
    }
}
